import React from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

interface TrendAnalysisSectionProps {
  completenessScore: number
  completenessTrend: any
  completenessChartData: any[]
  timelinessChartData: any[]
  pipelineChartData: any[]
  errorsWarningsData: any[]
  isCompletenessLoading: boolean
  trendTimeRange: '24h' | '7d' | '30d'
  handleTrendTimeRangeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
}

const TrendAnalysisSection: React.FC<TrendAnalysisSectionProps> = ({
  completenessScore,
  completenessTrend,
  completenessChartData,
  timelinessChartData,
  pipelineChartData,
  errorsWarningsData,
  isCompletenessLoading,
  trendTimeRange,
  handleTrendTimeRangeChange
}) => {
  // Debug logging
  console.log('errorsWarningsData:', errorsWarningsData)
  console.log('errorsWarningsData length:', errorsWarningsData?.length)
  
  return (
    <div className="trend-analysis-section">
      <div className="trend-header">
        <div className="trend-header-left">
          <h3 className="trend-title">Trend Analysis</h3>
          <p className="trend-subtitle">Displays the most recent trends</p>
        </div>
        <div className="trend-header-right">
          <select className="trend-time-dropdown" value={trendTimeRange} onChange={handleTrendTimeRangeChange}>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </div>
      </div>

      <div className="trend-metrics-grid">
        {/* Completeness Card */}
        <div className="trend-metric-card">
          <div className="trend-card-header">
            <span className="trend-metric-title">Completeness</span>
            <div className={`trend-indicator ${completenessTrend.isPositive ? 'positive' : 'negative'}`}>
              <span>{completenessTrend.isPositive ? '+' : '-'}{completenessTrend.percentage.toFixed(1)}%</span>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none"
                   xmlns="http://www.w3.org/2000/svg">
                <path d={completenessTrend.isPositive ? "M4 17L11 10L16 15L23 8" : "M4 8L11 15L16 10L23 17"}
                      stroke={completenessTrend.isPositive ? "#4CAF50" : "#E74C3C"} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                <path d={completenessTrend.isPositive ? "M23 8H17" : "M23 17H17"}
                      stroke={completenessTrend.isPositive ? "#4CAF50" : "#E74C3C"} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                <path d={completenessTrend.isPositive ? "M23 8V14" : "M23 17V11"}
                      stroke={completenessTrend.isPositive ? "#4CAF50" : "#E74C3C"} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
          <div className="trend-metric-value">
            <span className="trend-number">{completenessScore.toFixed(1)}</span>
            <span className="trend-unit">%</span>
          </div>
          <div className="trend-chart-container">
            {isCompletenessLoading ? (
              <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
                Loading...
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={80}>
                <AreaChart data={completenessChartData.length > 0 ? completenessChartData : [{ time: 'No data', value: 0 }]}>
                <defs>
                  <linearGradient id="colorCompleteness" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis
                  domain={[90, 100]}
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: '#374151', fontWeight: 600 }}
                  formatter={(value: any) => typeof value === 'number' ? [`${value.toFixed(1)}%`, 'Completeness'] : ['', '']}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#ef4444"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorCompleteness)"
                  animationDuration={1500}
                />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Timeliness Card */}
        <div className="trend-metric-card">
          <div className="trend-card-header">
            <span className="trend-metric-title">Timeliness</span>
            <div className="trend-indicator positive">
              <span>+5%</span>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none"
                 xmlns="http://www.w3.org/2000/svg">
                <path d="M4 17L11 10L16 15L23 8"
                      stroke="#4CAF50"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round" />
                <path d="M23 8H17"
                      stroke="#4CAF50"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round" />
                <path d="M23 8V14"
                      stroke="#4CAF50"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round" />
              </svg>
            </div>
          </div>
          <div className="trend-metric-value">
            <span className="trend-number">92</span>
            <span className="trend-unit">%</span>
          </div>
          <div className="trend-chart-container">
            <ResponsiveContainer width="100%" height={80}>
              <AreaChart data={timelinessChartData.length > 0 ? timelinessChartData : [{ time: 'No data', value: 0 }]}>
                <defs>
                  <linearGradient id="colorTrendTimeliness" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis
                  domain={[85, 100]}
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: '#374151', fontWeight: 600 }}
                  formatter={(value: any) => typeof value === 'number' ? [`${value.toFixed(1)}%`, 'Timeliness'] : ['', '']}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#22c55e"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorTrendTimeliness)"
                  animationDuration={1500}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pipeline success Card */}
        <div className="trend-metric-card">
          <div className="trend-card-header">
            <span className="trend-metric-title">Schema Drift</span>
            <div className="trend-indicator positive">
              <span>+6%</span>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none"
                 xmlns="http://www.w3.org/2000/svg">
                <path d="M4 17L11 10L16 15L23 8"
                      stroke="#4CAF50"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round" />
                <path d="M23 8H17"
                      stroke="#4CAF50"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round" />
                <path d="M23 8V14"
                      stroke="#4CAF50"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round" />
              </svg>
            </div>
          </div>
          <div className="trend-metric-value">
            <span className="trend-number">94</span>
            <span className="trend-unit">events</span>
          </div>
          <div className="trend-chart-container">
            <ResponsiveContainer width="100%" height={80}>
              <AreaChart data={pipelineChartData.length > 0 ? pipelineChartData : [{ time: 'No data', value: 0 }]}>
                <defs>
                  <linearGradient id="colorPipelineTrend" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis
                  domain={[90, 100]}
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: '#374151', fontWeight: 600 }}
                  formatter={(value: any) => typeof value === 'number' ? [`${value.toFixed(1)}%`, 'Success'] : ['', '']}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#22c55e"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorPipelineTrend)"
                  animationDuration={1500}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Error rate Card */}
        <div className="trend-metric-card">
          <div className="trend-card-header">
            <span className="trend-metric-title">Errors & Warnings rate</span>
            <div className="trend-indicator negative">
              <span>-12%</span>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none"
                   xmlns="http://www.w3.org/2000/svg">
                <path d="M4 8L11 15L16 10L23 17"
                      stroke="#E74C3C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M23 17H17"
                      stroke="#E74C3C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M23 17V11"
                      stroke="#E74C3C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
          <div className="trend-metric-value">
            <span className="trend-number">3</span>
            <span className="trend-unit">events</span>
          </div>
          <div className="trend-chart-container">
            {errorsWarningsData && errorsWarningsData.length > 0 ? (
              <ResponsiveContainer width="100%" height={80}>
                <AreaChart data={errorsWarningsData}>
                  <defs>
                    <linearGradient id="colorErrorsWarnings" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, 'auto']}
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                    labelStyle={{ color: '#374151', fontWeight: 600 }}
                    formatter={(value: any) => typeof value === 'number' ? [`${value}`, 'Events'] : ['', '']}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#22c55e"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorErrorsWarnings)"
                    animationDuration={1500}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ 
                height: '80px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#9ca3af',
                fontSize: '12px'
              }}>
                No chart data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TrendAnalysisSection